import React, { useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { baseUrl } from "../../../constants/api";
import { RootState } from "../../../store";

// ── Sample CSV content (6 columns only — topic/subtopic come from context) ────
const SAMPLE_CSV = `question,option_a,option_b,option_c,option_d,correct_option
"Who is the father of the Indian Railway?","Lord Dalhousie","Lord Curzon","Lord Mountbatten","Lord Rippon",A
"The first railway line in India was opened in which year?","1853","1857","1869","1875",A
"Which is the longest railway platform in India?","Gorakhpur","Kharagpur","Kollam","Surat",A
"What does 'IRCTC' stand for?","Indian Railway Catering and Tourism Corporation","Indian Rail Commerce and Travel Corporation","Indian Railway Central Ticket Corporation","Indian Rail Catering and Travel Company",A`;

function downloadSampleCSV() {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "sample_questions.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

interface BulkUploadResult {
    uploaded: number;
    skipped: number;
    errors: string[];
    message: string;
}

interface BulkUploadModalProps {
    folder: string;
    subfolder: string;
    onClose: () => void;
    onSuccess: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function BulkUploadModal({ folder, subfolder, onClose, onSuccess }: BulkUploadModalProps) {
    const token = useSelector((state: RootState) => state.account.token);

    const [file, setFile] = useState<File | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState<BulkUploadResult | null>(null); // { uploaded, skipped, errors, message }
    const [error, setError] = useState<string | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFile = useCallback((f: File | null) => {
        if (!f) return;
        if (!f.name.endsWith(".csv")) {
            setError("Please select a valid .csv file.");
            setFile(null);
            return;
        }
        setError(null);
        setResult(null);
        setFile(f);
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setDragOver(false);
            const dropped = e.dataTransfer.files[0];
            handleFile(dropped);
        },
        [handleFile]
    );

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setError(null);
        setResult(null);

        const formData = new FormData();
        formData.append("file", file);
        // Pass context so backend auto-fills topic/subtopic — user never needs to type them
        formData.append("topic_name", folder);
        formData.append("subtopic_name", subfolder);

        try {
            const response = await fetch(`${baseUrl}/api/questions/bulk-upload`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });

            const data = await response.json();

            if (response.ok) {
                setResult(data);
                setFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
                if (data.uploaded > 0 && onSuccess) onSuccess();
            } else {
                setError(data.message || "Upload failed. Please try again.");
            }
        } catch (err) {
            setError("Network error. Please check your connection.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="bulk-modal-overlay" onClick={onClose}>
            <div className="bulk-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="bulk-modal-header">
                    <div className="bulk-modal-title-group">
                        <div className="bulk-modal-icon">📤</div>
                        <div>
                            <h2 className="bulk-modal-title">Bulk Upload Questions</h2>
                            <p className="bulk-modal-subtitle">
                                Upload a CSV file to add multiple questions at once
                            </p>
                        </div>
                    </div>
                    <button className="bulk-modal-close" onClick={onClose} title="Close">✕</button>
                </div>

                {/* Context chips */}
                <div className="bulk-context-row">
                    <span className="bulk-chip topic-chip">📁 {folder}</span>
                    <span className="bulk-chip-arrow">›</span>
                    <span className="bulk-chip subtopic-chip">📂 {subfolder}</span>
                </div>

                {/* Sample download */}
                <div className="bulk-sample-bar">
                    <div className="bulk-sample-info">
                        <span className="bulk-sample-icon">📋</span>
                        <div>
                            <p className="bulk-sample-title">Need a template?</p>
                            <p className="bulk-sample-desc">
                                Download the sample CSV to see the required format
                            </p>
                        </div>
                    </div>
                    <button className="bulk-sample-btn" onClick={downloadSampleCSV}>
                        ⬇ Download Sample
                    </button>
                </div>

                {/* CSV format guide */}
                <div className="bulk-format-guide">
                    <p className="bulk-format-title">CSV columns (6 total — that's it!):</p>
                    <div className="bulk-columns-grid">
                        {["question", "option_a", "option_b", "option_c", "option_d", "correct_option"].map((col) => (
                            <span key={col} className="bulk-col-badge required">{col}</span>
                        ))}
                    </div>
                    <p className="bulk-format-note">
                        <strong>correct_option</strong>: A, B, C, or D &nbsp;·&nbsp;
                        Topic &amp; subtopic are auto-filled from the current context 🎯
                    </p>
                </div>

                {/* Drop zone */}
                <div
                    className={`bulk-dropzone ${dragOver ? "drag-active" : ""} ${file ? "has-file" : ""}`}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        style={{ display: "none" }}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (e.target.files) {
                                handleFile(e.target.files[0]);
                            }
                        }}
                    />
                    {file ? (
                        <div className="bulk-file-selected">
                            <span className="bulk-file-icon">📄</span>
                            <div className="bulk-file-info">
                                <p className="bulk-file-name">{file.name}</p>
                                <p className="bulk-file-size">{(file.size / 1024).toFixed(1)} KB</p>
                            </div>
                            <button
                                className="bulk-file-remove"
                                onClick={(e) => { e.stopPropagation(); setFile(null); setResult(null); setError(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                                title="Remove file"
                            >✕</button>
                        </div>
                    ) : (
                        <div className="bulk-dropzone-empty">
                            <div className="bulk-dropzone-icon">{dragOver ? "📂" : "☁️"}</div>
                            <p className="bulk-dropzone-text">
                                {dragOver ? "Drop your CSV here!" : "Drag & drop your CSV file here"}
                            </p>
                            <p className="bulk-dropzone-sub">or click to browse · Max 5 MB</p>
                        </div>
                    )}
                </div>

                {/* Error message */}
                {error && (
                    <div className="bulk-alert bulk-alert-error">
                        <span>⚠️</span> {error}
                    </div>
                )}

                {/* Success result */}
                {result && (
                    <div className="bulk-result">
                        <div className="bulk-result-stats">
                            <div className="bulk-stat success">
                                <span className="bulk-stat-num">{result.uploaded}</span>
                                <span className="bulk-stat-label">Uploaded</span>
                            </div>
                            <div className="bulk-stat warning">
                                <span className="bulk-stat-num">{result.skipped}</span>
                                <span className="bulk-stat-label">Skipped</span>
                            </div>
                        </div>
                        <p className="bulk-result-msg">✅ {result.message}</p>
                        {result.errors && result.errors.length > 0 && (
                            <div className="bulk-errors-list">
                                <p className="bulk-errors-title">⚠️ Skipped rows:</p>
                                <ul>
                                    {result.errors.map((err, i) => (
                                        <li key={i}>{err}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {/* Actions */}
                <div className="bulk-modal-actions">
                    <button className="bulk-btn-cancel" onClick={onClose}>
                        {result ? "Close" : "Cancel"}
                    </button>
                    {!result && (
                        <button
                            className="bulk-btn-upload"
                            onClick={handleUpload}
                            disabled={!file || uploading}
                        >
                            {uploading ? (
                                <><span className="bulk-spinner" />Uploading…</>
                            ) : (
                                "Upload Questions"
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
